import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120lSchBService } from './f1120l-sch-b.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120l-sch-b',
    templateUrl: './f1120l-sch-b.component.html',
    styleUrls: ['./f1120l-sch-b.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120lSchBComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Component properties migrated from FormScheduleBCtrl.js
  modalTitle = "Schedule B Investment Income (see instructions)";
  lossesIncurred = 0;
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  leId: string = '';
  formName = '1120L';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  
  // Flag to prevent multiple initialization
  private isInitialized = false;

  // Form data structure
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Entity and form data
  entityList: any[] = [];
  scheduleBFormView: any = { rows: [] };
  formListOriginal: any = {};
  unallocatedAmountData: any[] = [];
  unallocatedAmountDataOriginal: any[] = [];
  BatchList: any[] = [];
  BatchListData: any[] = [];

  // Tab configuration
  tabs = [
    { id: 1, name: "Form Entry", active: true },
    { id: 2, name: "Form View", active: false },
    { id: 3, name: "Audit", active: false }
  ];
  activeTab = 1;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120lSchBService: F1120lSchBService,
    private _snackBar: MatSnackBar,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {}
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      // Only proceed if we have row data and not already initialized
      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        console.log('Initializing F1120L-SCH-B component');
        this.isInitialized = true;
        this.setupEntityData();
        this.initScheduleB();
      }
    } catch (e) {
      console.error('Error in F1120L-SCH-B init:', e);
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
     
    // MANDATORY ORDER: Tax Year, LEID, LE Name, LE TYPE, Ledger, ME
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME},
      {key: "LE Type", value: this.rowData.LE_TYPE},
      {key: "Ledger", value: this.rowData.LEDGER},
      {key: "ME", value: this.rowData.ME}
    ];

    this.unallocatedAmountData = this.rowData.dig || [];
    // Create backup of original unallocated amount data for reset
    this.unallocatedAmountDataOriginal = _.cloneDeep(this.unallocatedAmountData);
  }

  initScheduleB() {
    // Load main data first, then parallel load supporting data
    this.fetchSchBrecords();
    this.fetchSchBimports();
  }

  fetchSchBrecords() {
    const params = { 
      "action_id": 603, 
      "tax_year": this.rowData.TAX_YEAR, 
      "scenario": this.rowData.SCENARIO, 
      "jcd_key": this.rowData.JCD_KEY, 
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  fetchSchBimports() {
    const params = { 
      "client_key": this.appService.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120L Sch B",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Batch data response:', data);
      this.BatchList = data.jsonObject || [];
      this.BatchList.forEach((value: any, key: number) => {
        this.BatchList[key].TOGGLE = false;
      });
      this.showRequestLoader = false;
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading batch data:', error);
      this.BatchList = [];
      this.showRequestLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    // CRITICAL: Reset the rows array before processing new data
    this.scheduleBFormView.rows = [];
    
    var dataRows = data.irsForm.formItemList;
    console.log('Processing form data, total rows:', dataRows.length);
    
    for(var i=1; i<dataRows.length; i++){
      var tempDataObj: any = {};
      
      // CRITICAL: Set data BEFORE calling iterateArray
      tempDataObj.line_order = dataRows[i].dataRowList[0].rowDimensions.object_id.split('~')[8];
      tempDataObj.saveParams = dataRows[i].dataRowList[0].rowDimensions.object_id.split('~');
      
      // THEN call iterateArray (which uses saveParams)
      this.iterateArray(i, tempDataObj, dataRows[i]);
      
      console.log('Adding row:', tempDataObj.num, tempDataObj.description);
      this.scheduleBFormView.rows.push(tempDataObj);
    }
    
    console.log('Final scheduleBFormView.rows length:', this.scheduleBFormView.rows.length);
    this.formListOriginal = _.cloneDeep(this.scheduleBFormView);
    this.computeValues();
    this.cdr.markForCheck();
  }

  iterateArray(index: number, tempDataObj: any, dataRow: any) {
    for(var j=0; j<dataRow.dataRowList[0].dataCellList.length; j++){
      if (j==0){
        tempDataObj.num = dataRow.dataRowList[0].dataCellList[j].name;
      } else if (j==1){
        tempDataObj.description = dataRow.dataRowList[0].dataCellList[j].name;
      }else{
        tempDataObj.lineAttrKey = dataRow.dataRowList[0].dataCellList[j].key.split('~')[1];
        tempDataObj.attrib_name = dataRow.dataRowList[0].dataCellList[j].key.split('~')[0];
        tempDataObj.is_editable = dataRow.dataRowList[0].dataCellList[j].property.is_editable_cell == 'Y' ? false : true;
        tempDataObj.is_display = dataRow.dataRowList[0].dataCellList[j].property.display == 'Y' ? true : false;
        const rawValue = dataRow.dataRowList[0].dataCellList[j].value;
        tempDataObj.value = rawValue == null ? 0 : Number(rawValue);
        
        // SAFE: Check if saveParams exists and has required length
        tempDataObj.is_form_display = tempDataObj.saveParams && tempDataObj.saveParams.length > 9  
          ? tempDataObj.saveParams[9]  
          : '';
      }
    }
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const params = { 
      "client_key": this.appService.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120L Sch B",
      "transaction_key": trans_key
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Transaction data response:', data);
      this.batchLoader = false;
      this.showBatchData = true;
      this.BatchListData = data.jsonObject || [];
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading transaction data:', error);
      this.batchLoader = false;
      this.showBatchData = false;
      this.BatchListData = [];
      this.cdr.markForCheck();
    });
  }

  private prev_index: number | null = null;

  showUploads(index: number, trans_key: string) {
    console.log('showUploads called:', {index, trans_key, prev_index: this.prev_index});
    
    if(this.prev_index !== null){
      if(this.prev_index !== index){
        console.log('Switching to different item');
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      }else{
        console.log('Toggling same item');
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = this.BatchList[index].TOGGLE;
        console.log('Toggle state:', this.BatchList[index].TOGGLE, 'showBatchData:', this.showBatchData);
      }
    }else{
      console.log('First time click');
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  /**
   * Resolve a Schedule B line by its printed line number (`row.num`).
   *
   * The previous implementation addressed lines purely by positional index
   * (line 6 == rows[5]). When the backend returns a different count/order of
   * rows for an entity, that assumption silently breaks: line 6 (and its
   * "Unallocated Amount" diagnostic) was never computed and stayed 0, while
   * line 2 (index 1) still happened to resolve. Look the line up by its number
   * first and only fall back to the positional index.
   */
  private getLineRow(lineNo: number): any {
    const rows = this.scheduleBFormView.rows || [];
    const target = String(lineNo);
    const byNum = rows.find((r: any) => String(r?.num ?? '').trim() === target);
    return byNum || rows[lineNo - 1];
  }

  private getLineValue(lineNo: number): number {
    return Number(this.getLineRow(lineNo)?.value) || 0;
  }

  computeValues() {
    const rows = this.scheduleBFormView.rows;
    if (!rows || rows.length === 0) {
      return;
    }

    // Schedule B line 6 (Gross investment income) = sum of lines 1 through 5.
    const line6Total = Math.round(
      this.getLineValue(1) +
      this.getLineValue(2) +
      this.getLineValue(3) +
      this.getLineValue(4) +
      this.getLineValue(5)
    );

    const line6Row = this.getLineRow(6);
    if (line6Row) {
      line6Row.value = line6Total;
    }

    // Update unallocated amount diagnostics. Guard every level so a missing
    // dig row never aborts the whole computation (mirrors the f1120l-sch-f/g
    // pattern) — otherwise the line 6 diagnostic could be skipped entirely.
    if (this.unallocatedAmountData && this.unallocatedAmountData.length > 0) {
      // Row 0 -> Schedule B line 2 (here and on Sch A line 20).
      if (this.unallocatedAmountData[0]?.[0]) {
        this.unallocatedAmountData[0][0].value = this.getLineValue(2);
      }
      if (this.unallocatedAmountData[0]?.[2]) {
        this.unallocatedAmountData[0][2].value =
          (this.unallocatedAmountData[0][0]?.value || 0) - (this.unallocatedAmountData[0][1]?.value || 0);
      }

      // Row 1 -> Schedule B line 6 (here and on Page 1 line 4).
      if (this.unallocatedAmountData[1]?.[0]) {
        this.unallocatedAmountData[1][0].value = line6Total;
      }
      if (this.unallocatedAmountData[1]?.[2]) {
        this.unallocatedAmountData[1][2].value =
          (this.unallocatedAmountData[1][0]?.value || 0) - (this.unallocatedAmountData[1][1]?.value || 0);
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    var formRowData = {};
    var tempArray = [];

    for(var i=0; i<this.scheduleBFormView.rows.length ;i++){
      formRowData = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleBFormView.rows[i].saveParams[1],
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleBFormView.rows[i].saveParams[2],
        line_no: this.scheduleBFormView.rows[i].num,
        line_order: this.scheduleBFormView.rows[i].line_order,
        occurence: this.scheduleBFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleBFormView.rows[i].lineAttrKey,
        line_description: this.scheduleBFormView.rows[i].description,
        Attribute_Name: this.scheduleBFormView.rows[i].attrib_name,
        Attribute_Value: this.scheduleBFormView.rows[i].value,
        trans_dtls_key: this.scheduleBFormView.rows[i].saveParams[3],
        trans_status: this.scheduleBFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowData);
    }
    this.formData.editrows.push(tempArray);

    const mainData = [];
    mainData.push(this.formData);

    // Build diagnostic data
    const diagData: any[] = [];
    if (this.unallocatedAmountData) {
      this.unallocatedAmountData.forEach((array: any[], key: number) => {
        array.forEach((obj: any, key: number) => {
          if(key === 3){
            diagData.push(obj);
          }
        });
      });
    }

    this.f1120lSchBService.saveF1120lSchBForm(
      this.GENERAL_CONFIG.base_url, 
      mainData, 
      diagData,
      this.globalService, 
      this.filterParams, 
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {  
            duration: 4000, 
            panelClass: ['success-snackbar'] 
          });
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {  
            duration: 4000, 
            panelClass: ['error-snackbar'] 
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {  
          duration: 4000, 
          panelClass: ['error-snackbar'] 
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    // Reset main form
    this.scheduleBFormView = _.cloneDeep(this.formListOriginal);
    
    // Reset unallocated amount data
    this.unallocatedAmountData = _.cloneDeep(this.unallocatedAmountDataOriginal);
    
    // Recalculate computed values
    this.computeValues();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  // Update value method to ensure proper number conversion
  updateValue(row: any, event: any) {
    const inputElement = event.target as HTMLInputElement;
    const rawValue = inputElement.value;
    const numValue = Number(rawValue) || 0;
    
    console.log(`UpdateValue for ${row.num}: raw="${rawValue}", parsed=${numValue}`);
    
    row.value = numValue;
    this.computeValues();
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.lineAttrKey || item.num || index : index;
  }
}

